/**
 * Single source of truth for mapping the curriculum catalogue's free-form
 * textbook subject names (`"Science"`, `"Science (Curiosity)"`,
 * `"Mathematics"`, `"Maths (Ganita Prakash)"`, `"Social Science
 * (Exploring Society)"`, `"English (First Flight)"`, …) to the
 * `CreateLessonPackDto.subject` enum slot the lesson-pack generator
 * accepts.
 *
 * Per the backend doc (CURRICULUM_API.md §4 "Subject mapping note") and the
 * DO-NOT list in the FE brief: don't hardcode this mapping in multiple
 * places. Every call site (CurriculumPicker, NewLessonPackPage, the
 * Curriculum admin page if it ever needs to preview the enum) imports the
 * helper from here.
 *
 * NOTE: this does NOT merge subject names client-side ("Science" and
 * "Science (Curiosity)" stay as separate dropdown rows — they're distinct
 * textbooks). The mapping is only applied at the moment we hand off to the
 * lesson-pack generator, whose `subject` field is a constrained enum.
 */

import type { LessonPackSubject } from './schema.types';

/**
 * The closed set of values `CreateLessonPackDto.subject` accepts. Mirrors
 * `LessonPackSubject` minus the `(string & {})` escape hatch so we have
 * an exhaustive list at runtime.
 */
export const LESSON_PACK_SUBJECT_ENUM = [
  'math',
  'science',
  'physics',
  'chemistry',
  'biology',
  'kids',
  'history',
  'language',
  'general',
] as const satisfies readonly LessonPackSubject[];

export type LessonPackSubjectEnum = (typeof LESSON_PACK_SUBJECT_ENUM)[number];

/**
 * Best-effort map: curriculum-catalogue subject string → lesson-pack enum.
 *
 * Order of checks matters: most specific first ("social science" must beat
 * "science"; "biology" must beat "science" because some boards label life-
 * science chapters as "Science"). Free-text manual overrides (when the
 * teacher types instead of picking from the catalogue) go through the same
 * function — typo-tolerant by design (lowercases + substring matches).
 *
 * Fallback is `'general'`, which the generator handles safely.
 */
export function subjectToLessonPackEnum(
  freeFormSubject: string | undefined | null,
): LessonPackSubjectEnum {
  if (!freeFormSubject) return 'general';
  const s = freeFormSubject.toLowerCase();

  // Most specific first.
  if (s.includes('physics')) return 'physics';
  if (s.includes('chemistry')) return 'chemistry';
  if (s.includes('biology') || s.includes('life science')) return 'biology';

  if (s.includes('social') || s.includes('history') || s.includes('civics') || s.includes('geograph') || s.includes('political')) {
    return 'history';
  }

  if (s.includes('english') || s.includes('language') || s.includes('hindi') || s.includes('sanskrit')) {
    return 'language';
  }

  if (s.includes('math') || s.includes('maths') || s.includes('ganit')) return 'math';

  if (s.includes('science') || s.includes('curiosity')) return 'science';

  if (s.includes('kid') || s.includes('preschool') || s.includes('foundational')) return 'kids';

  return 'general';
}

/**
 * Human-readable label for the enum value — used when displaying a "(maps
 * to: science)" hint under a free-text subject input, or in the admin
 * page's preview column. Keeps the enum names from leaking raw into the
 * UI when we don't need them to.
 */
export function lessonPackSubjectLabel(value: LessonPackSubjectEnum): string {
  switch (value) {
    case 'math': return 'Mathematics';
    case 'science': return 'Science';
    case 'physics': return 'Physics';
    case 'chemistry': return 'Chemistry';
    case 'biology': return 'Biology';
    case 'kids': return 'Kids';
    case 'history': return 'Social Studies / History';
    case 'language': return 'Language';
    case 'general': return 'General';
  }
}
